import { easeBackOut, easeCubicInOut } from 'd3-ease';
import { useCallback, useMemo, useRef } from 'react';
import { MathUtils } from 'three';
import type { GeoNode } from '../location';
import type { SceneDriver, SceneTransform } from '../scene';
import { framing, globeFraming, lerpFraming, worldFraming } from '../scene';
import { GLOBE_TILT, INTRO_SCALE, INTRO_ZOOM_OUT, TIMING } from './constants';
import { faceAngles } from './faceAngles';
import { TIMELINE } from './timeline';

const clamp01 = (x: number) => MathUtils.clamp(x, 0, 1);

export function useIntroDriver({
  node,
  onConnected,
}: {
  node: GeoNode;
  onConnected: () => void;
}): SceneDriver {
  const angles = useMemo(() => faceAngles(node.lat, node.lon), [node]);

  const startRef = useRef<number | null>(null);
  const connectedRef = useRef(false);
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  const scratch = useMemo(
    () => ({
      globe: framing(),
      from: framing(),
      world: framing(),
      out: {
        framing: framing(),
        scale: 1,
        yaw: 0,
        pitch: 0,
      } as SceneTransform,
    }),
    []
  );

  return useCallback<SceneDriver>(
    ({ time, viewport, channels }) => {
      const { globe, from, world, out } = scratch;

      startRef.current ??= time;
      const elapsed = time - startRef.current;

      const morph = easeCubicInOut(
        clamp01((elapsed - TIMELINE.flattenStart) / TIMING.flatten)
      );
      channels.morph.current = morph;

      const growIn = easeCubicInOut(clamp01(elapsed / TIMING.globeFadeIn));
      out.scale = INTRO_SCALE + (1 - INTRO_SCALE) * growIn;
      channels.coastlineOpacity.current = growIn;

      globeFraming(viewport, 1, globe);
      if (elapsed < TIMELINE.flattenStart) {
        globeFraming(viewport, INTRO_ZOOM_OUT, from);
        const easeIn = easeCubicInOut(clamp01(elapsed / TIMELINE.flattenStart));
        lerpFraming(from, globe, easeIn, out.framing);
      } else {
        worldFraming(viewport, world);
        lerpFraming(globe, world, morph, out.framing);
      }

      const upright = 1 - morph;
      out.yaw = angles.yaw * upright;
      out.pitch = (GLOBE_TILT + angles.pitch) * upright;

      const pop = clamp01((elapsed - TIMELINE.dotPopAt) / TIMING.dotPop);
      channels.dotReveal.current = pop <= 0 ? 0 : easeBackOut(pop);

      channels.timezoneOpacity.current = easeCubicInOut(
        clamp01((elapsed - TIMELINE.timezoneFadeStart) / TIMING.timezoneFade)
      );

      if (!connectedRef.current && elapsed >= TIMELINE.dotPopAt) {
        connectedRef.current = true;
        onConnectedRef.current();
      }

      return out;
    },
    [angles, scratch]
  );
}
